import { Injectable } from '@angular/core';
import { Competencia, DateUtils, HttpClient, Page, PageParameters } from '@elotech/angular-common';
import { Observable, of } from 'rxjs';
import { EsocialService } from '../common/services/esocial-service';
import { TipoDeCalculo } from './form/core/tipo-de-calculo.enum';
import { ProgramacaoFeriasDTO } from './programacao-ferias-dto';
import { OcorrenciaPeriodoAquisitivo } from './OcorrenciaPeriodoAquisitivo';
import { Funcionario } from '../lancamento/model';
import { TipoMovimentacaoEnum } from '../common/enum/tipo-movimentacao-enum';
import { EnumItem } from '../common/enum-item';

const MODELFORMAT = 'YYYY-MM-DD';

@Injectable()
export class ProgramacaoFeriasService extends EsocialService<ProgramacaoFerias> {

  constructor(public httpClient: HttpClient) {
    super(httpClient);
    this.resource = '/api/programacao-ferias';
  }

  public findAll(page?: PageParameters, search?: string, fields?: string, disableSpinner?: boolean): Observable<Page<any>> {
    return this.httpClient.get({
      resource: `${this.resource}/findAllDto`,
      page,
      search: this.buildSearch(search),
      disableSpinner,
      location: this.location
    });
  }

  public salvar(programacaoFerias: ProgramacaoFeriasDTO): Observable<any> {
    return this.httpClient.post({
      body: programacaoFerias,
      resource: `${this.resource}`,
    });
  }

  public findAllDTOByPeriodosAquisitivosIds(periodoAquisitivoIds: number[], disableSpinner: boolean = false): Observable<ProgramacaoFeriasDTO[]> {
    return this.httpClient.get({
      disableSpinner,
      resource: `${this.resource}/dto/periodos-aquisitivos/${periodoAquisitivoIds.join(',')}`
    });
  }

  public loadFeriasComConflito(funcionarioId: number, dataInicial: Date, dataFinal: Date): Observable<ProgramacaoFeriasDTO[]> {
    if (!funcionarioId) {
      return of();
    }

    const body = {
      dataInicial: DateUtils.format(dataInicial, MODELFORMAT, MODELFORMAT),
      dataFinal: DateUtils.format(dataFinal, MODELFORMAT, MODELFORMAT)
    };

    return this.httpClient.post({
      resource: `${this.resource}/dto/ferias-com-conflito/${funcionarioId}`,
      body,
      disableSpinner: true
    });
  }

  public loadDashboardFerias(competencia: any): Observable<any> {
    return this.httpClient.post({
      resource: `${this.resource}/dashboard`,
      body: competencia,
      disableSpinner: true
    });
  }

  public findReciboProgramacaoFerias(id: number): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}/recibo/${id}`
    });
  }

  public findPeriodoProgramacaoFerias(idProgramacaoFerias: number): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}/periodo-recibo/${idProgramacaoFerias}`
    });
  }

  public verificaDireitoProgramacaoFerias(funcionarioId: number): Observable<boolean> {
    return this.httpClient.get({
      resource: `${this.resource}/tem-direito/${funcionarioId}`,
      disableSpinner: true,
    });
  }

  public findNextYearByFuncionarioIdEntidadeId(funcionarioId: number, disableSpinner: boolean = false) {
    return this.httpClient.get({
      disableSpinner,
      resource: `${this.resource}/funcionario/${funcionarioId}`
    });
  }

  exportReport(params: any, extensao: string, report: string): Observable<any> {
    return this.httpClient.post({
      resource: `${this.resource}/${report}/${extensao}`,
      body: params,
      responseType: 'blob',
    });
  }

  public isFolhaMensalComFerias(calculoId: number): Observable<boolean> {
    return this.httpClient.get({
      resource: `${this.resource}/is-folha-mensal-com-ferias/${calculoId}`,
      disableSpinner: true,
    });
  }

  public findOneDTO(id: number): Observable<ProgramacaoFeriasDTO> {
    return this.httpClient.get({
      disableSpinner: true,
      resource: `${this.resource}/dto/${id}`,
    });
  }

  public findByMovimentacaoId(id: number): Observable<ProgramacaoFeriasDTO> {
    return this.httpClient.get({
      resource: `${this.resource}/movimentacao/${id}`,
    });
  }

  existeOutraFeriasComDiasEmDireitoParaOAquisitivo(aquisitivoId: number, feriasId?: number): Observable<boolean> {
    let queryParams = '';
    if (feriasId) {
      queryParams += `?feriasId=${feriasId}`;
    }
    return this.httpClient.get({
      resource: `${this.resource}/exists/dias-em-direito-no-aquisitivo/${aquisitivoId}${queryParams}`,
      disableSpinner: true
    });
  }

  findTipoCalculoComDiasDireitoNoAquisitivo(aquisitivoId: number, feriasId?: number): Observable<TipoDeCalculo | null> {
    let queryParams = feriasId ? `?feriasId=${feriasId}` : '';

    return this.httpClient.get({
      resource: `${this.resource}/tipo-calculo-dias-direito/${aquisitivoId}${queryParams}`,
      disableSpinner: true
    });
  }

}

export interface ProgramacaoFerias {
  tipoDeCalculo: EnumItem;
  fimGozo: string;
  inicioGozo: string;
  id: number;
  competencia: Competencia;
  periodoAquisitivo: PeriodoAquisitivo;
  movimentacao: MovimentacaoPessoal;
  diasPecunia: number;
  dataCadastro: Date;
}

export interface MovimentacaoPessoal {
  atosLegais: any[];
  arquivosMovimentacao: any[];
  id: number;
  tipoMovimentacao: TipoMovimentacaoEnum;
  funcionario: Funcionario;
  descricao: string;
}

export interface PeriodoAquisitivo {
  id: number,
  dataInicio: Date | string,
  dataFim: Date | string,
  diasDireito: number,
  diasFaltas: number,
  diasAfastamento: number,
  ocorrencias: OcorrenciaPeriodoAquisitivo[]
}
